import React, { useState, useRef, useEffect } from 'react';
import { Input, Typography } from 'antd';
import { AsYouType } from 'libphonenumber-js';
import firebase from 'firebase/app';

import {
  COUNTRIES, getPlaceholder, parseE164, nationalFromE164, formatE164ForDisplay, defaultCountry,
} from '../../utils/phone';
import { phoneAuthErrorMessage } from '../../utils/identity';
import { trackSmsVerificationRequested, trackSmsVerificationCompleted } from '../../utils/smsTracking';

import { signupControl, signupPrimary, signupTextLink } from './SignupShell';
import NativeSelect from './NativeSelect';

const { Text } = Typography;

const darkField = {
  ...signupControl,
  backgroundColor: '#1a1a1a',
  color: '#fff',
  borderColor: '#444',
};

/**
 * Phone-first sign-up: the mobile number IS the credential.
 *
 * Two asks, one per screen: the number, then the 6-digit code. Firebase phone
 * auth is "sign in or create", so a number already on an account signs that
 * person straight in (authResult.additionalUserInfo.isNewUser === false) and
 * the parent routes them on without ever offering a second account — that is
 * the duplicate-account guard for this door. A new number mints the auth user
 * and the parent writes the client role + the number's country.
 *
 * The reCAPTCHA verifier is invisible: Firebase requires one for web phone
 * auth, but nothing is drawn or tapped unless Google challenges the device.
 *
 * Props
 *  - defaultCountryCode: opening country for the picker (venue currency hint,
 *    else locale). The person can change it.
 *  - initialPhone: an E.164 number already typed elsewhere (the login lookup),
 *    so it is never typed twice.
 *  - ctaLabel: the send button's label ("Join Marble City Bar Staff").
 *  - onSuccess(authResult, { countryCode }): after the code is confirmed.
 */
export default function PhoneFirstSignup({
  defaultCountryCode, initialPhone, ctaLabel = 'Continue', onSuccess, trackingContext = 'signup',
}) {
  const prefill = initialPhone ? nationalFromE164(initialPhone) : null;
  const [step, setStep] = useState('enter'); // 'enter' | 'code'
  const [country, setCountry] = useState(
    () => (prefill && COUNTRIES.find((c) => c.code === prefill.country)) || defaultCountry(defaultCountryCode),
  );
  const [raw, setRaw] = useState(() => (prefill ? prefill.national : ''));
  // The venue's currency hint can arrive after first paint (the tag is read
  // async). Follow it until the person touches the picker or types a digit.
  const [touched, setTouched] = useState(!!prefill);
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [resendCooldown, setResendCooldown] = useState(0);
  const confirmationRef = useRef(null);
  const recaptchaRef = useRef(null);
  const verifierRef = useRef(null);
  const codeInputRef = useRef(null);

  useEffect(() => {
    if (touched || !defaultCountryCode) return;
    const hinted = COUNTRIES.find((c) => c.code === String(defaultCountryCode).toUpperCase());
    if (hinted) setCountry(hinted);
  }, [defaultCountryCode, touched]);

  useEffect(() => {
    if (resendCooldown <= 0) return undefined;
    const t = setTimeout(() => setResendCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [resendCooldown]);

  // The code screen opens with the keyboard already up.
  useEffect(() => {
    if (step === 'code' && codeInputRef.current && codeInputRef.current.focus) {
      codeInputRef.current.focus();
    }
  }, [step]);

  // Tear down the invisible widget with the component.
  useEffect(() => () => {
    try { if (verifierRef.current && verifierRef.current.clear) verifierRef.current.clear(); } catch (_) { /* noop */ }
  }, []);

  const e164 = parseE164(raw, country.code);
  const isValid = !!e164;

  const handleRawChange = (e) => {
    const formatter = new AsYouType(country.code);
    setRaw(formatter.input(e.target.value));
    setTouched(true);
    setError(null);
  };

  const sendCode = async () => {
    if (!e164 || loading) return undefined;
    setError(null);
    setLoading(true);
    try {
      const container = recaptchaRef.current;
      if (!container) throw new Error('reCAPTCHA container not ready');
      try { if (verifierRef.current && verifierRef.current.clear) verifierRef.current.clear(); } catch (_) { /* noop */ }
      container.innerHTML = '';
      const verifier = new firebase.auth.RecaptchaVerifier(container, { size: 'invisible' });
      verifierRef.current = verifier;
      const result = await firebase.auth().signInWithPhoneNumber(e164, verifier);
      confirmationRef.current = result;
      trackSmsVerificationRequested({ e164, inputCountry: country.code, context: trackingContext });
      setCode('');
      setStep('code');
      setResendCooldown(30);
    } catch (err) {
      setError(phoneAuthErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const confirmCode = async (value) => {
    const digits = (value != null ? value : code).trim();
    if (digits.length !== 6 || !confirmationRef.current || loading) return;
    setError(null);
    setLoading(true);
    try {
      const authResult = await confirmationRef.current.confirm(digits);
      trackSmsVerificationCompleted({ e164, inputCountry: country.code, context: trackingContext });
      if (onSuccess) await onSuccess(authResult, { countryCode: country.code });
    } catch (err) {
      setError(phoneAuthErrorMessage(err));
      setLoading(false);
    }
  };

  const handleCodeChange = (e) => {
    const digits = e.target.value.replace(/\D/g, '').slice(0, 6);
    setCode(digits);
    setError(null);
    // Six digits in → go. One less tap on a phone.
    if (digits.length === 6) confirmCode(digits);
  };

  const changeNumber = () => {
    setStep('enter');
    setCode('');
    setError(null);
    confirmationRef.current = null;
  };

  if (step === 'code') {
    return (
      <CodeScreen
        e164={e164}
        code={code}
        loading={loading}
        error={error}
        resendCooldown={resendCooldown}
        codeInputRef={codeInputRef}
        recaptchaRef={recaptchaRef}
        onCodeChange={handleCodeChange}
        onConfirm={() => confirmCode()}
        onResend={sendCode}
        onChangeNumber={changeNumber}
      />
    );
  }

  return (
    <NumberScreen
      country={country}
      raw={raw}
      isValid={isValid}
      loading={loading}
      error={error}
      ctaLabel={ctaLabel}
      recaptchaRef={recaptchaRef}
      onCountryChange={(c) => {
        setCountry(COUNTRIES.find((x) => x.code === c) || COUNTRIES[0]);
        setTouched(true);
        setRaw('');
        setError(null);
      }}
      onRawChange={handleRawChange}
      onSend={sendCode}
    />
  );
}

function CodeScreen({
  e164, code, loading, error, resendCooldown, codeInputRef, recaptchaRef,
  onCodeChange, onConfirm, onResend, onChangeNumber,
}) {
  const blocked = code.length !== 6 || loading;
  return (
    <div>
      <Text style={{ color: '#fff', fontSize: 16, display: 'block', marginBottom: 12 }}>
        We texted a code to <strong>{formatE164ForDisplay(e164)}</strong>.
      </Text>
      <Input
        ref={codeInputRef}
        placeholder="6-digit code"
        value={code}
        onChange={onCodeChange}
        onPressEnter={onConfirm}
        maxLength={6}
        inputMode="numeric"
        pattern="[0-9]*"
        autoComplete="one-time-code"
        disabled={loading}
        style={{
          ...darkField, textAlign: 'center', letterSpacing: 8, fontSize: 24, height: 56,
        }}
      />
      <button
        type="button"
        onClick={onConfirm}
        disabled={blocked}
        style={{ ...signupPrimary, marginTop: 12, opacity: blocked ? 0.5 : 1, cursor: blocked ? 'default' : 'pointer' }}
      >
        {loading ? 'Checking…' : 'Confirm'}
      </button>
      {error && (
        <Text type="danger" style={{ fontSize: 16, display: 'block', marginTop: 10 }}>{error}</Text>
      )}
      <button
        type="button"
        onClick={onResend}
        disabled={resendCooldown > 0 || loading}
        style={{ ...signupTextLink, color: resendCooldown > 0 ? '#666' : '#bbb', marginTop: 8 }}
      >
        {resendCooldown > 0 ? `Resend code in ${resendCooldown}s` : 'Resend code'}
      </button>
      <button type="button" onClick={onChangeNumber} disabled={loading} style={{ ...signupTextLink, color: '#bbb' }}>
        Use a different number
      </button>
      <div ref={recaptchaRef} />
    </div>
  );
}

function NumberScreen({
  country, raw, isValid, loading, error, ctaLabel, recaptchaRef,
  onCountryChange, onRawChange, onSend,
}) {
  const blocked = !isValid || loading;
  return (
    <div>
      <Input.Group compact style={{ display: 'flex' }}>
        {/* The browser's own picker: no inner-scrolling dropdown on a phone. */}
        <NativeSelect
          dark
          ariaLabel="Country code"
          value={country.code}
          onChange={(c) => onCountryChange(c || country.code)}
          disabled={loading}
          className="signup-country"
          style={{ width: 118, flexShrink: 0, borderRadius: '8px 0 0 8px', paddingRight: 28 }}
          options={COUNTRIES.map((c) => ({ value: c.code, label: `${c.flag} ${c.dial}` }))}
        />
        <Input
          placeholder={getPlaceholder(country.code)}
          value={raw}
          onChange={onRawChange}
          onPressEnter={onSend}
          size="large"
          type="tel"
          inputMode="tel"
          autoComplete="tel-national"
          autoFocus
          disabled={loading}
          style={{ ...darkField, flex: 1, borderRadius: '0 8px 8px 0' }}
        />
      </Input.Group>
      <div ref={recaptchaRef} />
      <button
        type="button"
        onClick={onSend}
        disabled={blocked}
        style={{ ...signupPrimary, marginTop: 12, opacity: blocked ? 0.5 : 1, cursor: blocked ? 'default' : 'pointer' }}
      >
        {loading ? 'Sending code…' : ctaLabel}
      </button>
      <Text style={{ color: '#888', fontSize: 16, display: 'block', marginTop: 12, textAlign: 'center' }}>
        We’ll text you a code. No password needed.
      </Text>
      {error && (
        <Text type="danger" style={{ fontSize: 16, display: 'block', marginTop: 10, textAlign: 'center' }}>{error}</Text>
      )}
    </div>
  );
}
